import React from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';
import {
  Login as LoginIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Logout Page Component
 * 
 * A confirmation page displayed after a user logs out of the application.
 * Provides feedback about successful logout and options to log back in.
 * 
 * @component
 * @returns {JSX.Element} The logout confirmation page component
 * 
 * @example
 * <Route path="/logout" element={<LogoutPage />} />
 * 
 * @description
 * This component provides:
 * - Clear feedback about successful logout
 * - Option to navigate back to the login page
 * - Clean, responsive UI with appropriate icons
 * - Consistent styling with the rest of the application
 */
const LogoutPage: React.FC = () => {
  const navigate = useNavigate();

  /**
   * Handles navigation to the login page
   * @function handleLogin
   * @returns {void}
   * @description
   * - Navigates the user to the login page
   * - Uses react-router's navigate function for client-side routing
   */
  const handleLogin = (): void => {
    navigate('/login');
  };

  return (
    // Main container with responsive max width
    <Container maxWidth="md" sx={{backgroundColor:'#F4F5F9',height:'100vh',display:'flex',justifyContent:'center',alignItems:'center',flexDirection:'column',width:'70%'}}>
      {/* Centered content container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh', // Ensure the content is vertically centered
          py: 4, 
         minWidth: '70%',
          boxShadow: 'none',
        }}
      >
        {/* Card container for the logout message */}
        <Card
          sx={{
            maxWidth: 500, // Maximum width of the card
            width: '100%', // Full width on smaller screens
            borderRadius: 3, // Rounded corners
            textAlign: 'center', // Center align text content
           background: '#ffff',
          boxShadow: 'none',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            

            {/* Main Heading */}
            <Typography
              variant="h4"
              component="h1"
              sx={{
                mb: 2,
                color: 'text.primary',
                fontWeight: 400,
              }}
            >
              Logged Out
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {/* Message */}
            <Typography
              variant="body1"
              sx={{
                mb: 4,
                color: 'text.secondary',
                lineHeight: 1.6,
              }}
            >
             Thank you for using EB Industries.
            </Typography>

            {/* Additional Info */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}
              >
                Your session has ended successfully.
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                variant="contained"
                onClick={handleLogin}
                startIcon={<LoginIcon sx={{ color: 'white' }} />}
                sx={{
                  minWidth: 140,
                  bgcolor: '#1b184d',
                  color: '#ffff',
                  
                }}
              >
                Login
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default LogoutPage;
